import React, { useCallback, useMemo } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { GenericDropdownOption } from '../../../../../../App.types';
import { TiktokAudioCollection, patchCollection, uploadCollectionImage } from '@round/api';
import Modal, { ModalContent, ModalFooter, ModalTitle } from '../../../../../../ui/Layout/Modals/Modal/Modal';
import CollectionForm, { CollectionFormValues } from '../components/CollectionForm/CollectionForm';
import Button from '../../../../../../ui/Buttons/Button/Button';
import { showNotification } from '../../../../../../helpers';
import useDownloadImage from '../../../../../../Hooks/useDownloadImage';
import { mapApiErrorsToFormikErrors } from '../../../../../../utility/utility';

type EditCollectionModalProps = {
    isModalOpen: boolean;
    closeModal: () => void;
    collection: TiktokAudioCollection;
    clientOptions: GenericDropdownOption<number>[];
    onUpdated: (collection: TiktokAudioCollection) => void;
};

const EditCollectionModal = ({
    isModalOpen,
    closeModal,
    collection,
    clientOptions,
    onUpdated,
}: EditCollectionModalProps) => {
    const { image: imageFile } = useDownloadImage(collection.picture);
    const initialValues: CollectionFormValues = useMemo(
        () => ({
            name: collection.name,
            client: clientOptions.find((option) => option.value === collection.client) ?? null,
            image: imageFile ? [imageFile] : [],
        }),
        [clientOptions, collection.client, collection.name, imageFile]
    );

    const handleSubmit = useCallback(
        async (values: CollectionFormValues, { setSubmitting, setErrors }: FormikHelpers<CollectionFormValues>) => {
            if (values.client === null) {
                return;
            }

            try {
                setSubmitting(true);
                let updatedCollection: TiktokAudioCollection = collection;
                if (values.client.value !== collection.client || values.name !== collection.name) {
                    const response = await patchCollection(collection.id, {
                        client: values.client.value,
                        name: values.name,
                    });

                    if (response.status === 404) {
                        showNotification('Could not update collection. Collection not found', 'error');
                        return;
                    }

                    if (response.status === 400) {
                        setErrors(mapApiErrorsToFormikErrors(response.data));
                        return;
                    }

                    updatedCollection = response.data;
                }

                const image = values.image[0];
                if (image && image !== initialValues.image[0]) {
                    const response = await uploadCollectionImage(collection.id, image);
                    if (response.status === 404) {
                        showNotification('Could not upload image. Collection not found.', 'error');
                        return;
                    }

                    updatedCollection = response.data;
                }

                onUpdated(updatedCollection);
                closeModal();
                showNotification('Collection updated', 'info');
            } catch {
                showNotification('Could not update collection', 'error');
            } finally {
                setSubmitting(false);
            }
        },
        [closeModal, collection, initialValues.image, onUpdated]
    );

    return (
        <Modal closeOnOverlayClick isOpen={isModalOpen} onClose={closeModal}>
            <ModalTitle>Edit collection</ModalTitle>
            <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
                {({ isSubmitting, submitForm, dirty }) => (
                    <>
                        <ModalContent>
                            <CollectionForm clientOptions={clientOptions} />
                        </ModalContent>
                        <ModalFooter>
                            <Button
                                type="filled"
                                color="black"
                                loading={isSubmitting}
                                disabled={!dirty}
                                onClick={submitForm}
                            >
                                Save
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </Formik>
        </Modal>
    );
};

export default EditCollectionModal;
